/**
 * Заключение по сделке и подтверждение расхождения (BE-252, BE-253).
 *
 * Два правила слоя:
 *
 * * **подтверждение расхождения — необратимое по смыслу действие** и идёт без
 *   оптимистичного UI (CLAUDE.md §8): оно снимает блокировку согласия, и
 *   рисовать разблокированную кнопку до ответа сервера нельзя;
 * * **ответ мутации кладётся в кэш**: он и есть новое состояние
 *   `GET /conclusion`, посчитанное в той же транзакции.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { apiFetch } from '@shared/api/client';
import { queryKeys } from '@shared/api/queryKeys';
import { randomUuid } from '@shared/lib/uuid';

import type { ConclusionSet } from './types';

function base(searchId: string): string {
  return `/search-sessions/${encodeURIComponent(searchId)}/conclusion`;
}

/**
 * Заключение по сделке (шаг 6, §38.2).
 *
 * Пустой `items` — **валидное состояние**, а не ошибка: торг ещё не дошёл до
 * финального предложения, и экран показывает ожидание.
 */
export function useConclusion(searchId: string | null, enabled = true) {
  return useQuery({
    queryKey: queryKeys.conclusion(searchId ?? ''),
    queryFn: () => apiFetch<ConclusionSet>(base(searchId ?? '')),
    enabled: enabled && searchId !== null && searchId !== '',
    retry: false,
  });
}

/**
 * «Я видел расхождение» — снимает блокировку согласия и **ничего больше**.
 *
 * Подтверждение не означает согласия с расхождением, не закрывает его и не
 * убирает с экрана (§38.3). Карточка переписки инвалидируется вместе с
 * заключением: кнопка «Согласиться» живёт там.
 */
export function useAcknowledgeDiscrepancy(searchId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (vehicleId: string) =>
      apiFetch<ConclusionSet>(`${base(searchId)}/acknowledge`, {
        method: 'POST',
        idempotencyKey: randomUuid(),
        body: { vehicle_id: vehicleId },
      }),
    onSuccess: (state) => {
      queryClient.setQueryData(queryKeys.conclusion(searchId), state);
      void queryClient.invalidateQueries({ queryKey: queryKeys.negotiations() });
    },
  });
}
